import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
} from "@mui/material";
import { Row, Col } from "react-bootstrap";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import dayjs from "dayjs";
import MapLocation from "./mapLocation/mapLocation";

const BookingForm = ({
  open,
  onClose,
  booking,
  onSubmit,
  serviceList,
  setLatitude,
  setLongitude,
  latitude,
  longitude,
}) => {
  const [formData, setFormData] = useState({
    serviceName: "",
    customerName: "",
    phone: "",
    address: "",
    location: "",
    bookingDate: null,
    bookingTime: null,
  });

  const [errors, setErrors] = useState({});

  const serviceNamesWithIds = serviceList.map((service) => ({
    id: service.id,
    name: service.service,
  }));

  useEffect(() => {
    if (booking) {
      setFormData({
        serviceName: booking.service_id,
        customerName: booking.name,
        phone: booking.phone,
        address: booking.address,
        bookingDate: booking.booked_date
          ? dayjs(booking.booked_date, "DD-MM-YYYY")
          : null,
        bookingTime: booking.booked_time
          ? dayjs(booking.booked_time, "HH:mm:ss")
          : null,
      });
      setLatitude(booking.latitude);
      setLongitude(booking.longitude);
    } else {
      setFormData({
        serviceName: "",
        customerName: "",
        phone: "",
        address: "",
        bookingDate: null,
        bookingTime: null,
      });
      setLatitude(null);
      setLongitude(null);
    }
    setErrors({});
  }, [booking, setLatitude, setLongitude]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleDateChange = (date) => {
    setFormData((prevData) => ({
      ...prevData,
      bookingDate: date,
    }));
  };

  const handleTimeChange = (time) => {
    setFormData((prevData) => ({
      ...prevData,
      bookingTime: time,
    }));
  };

  const validation = () => {
    const newError = {};
    if (!formData.serviceName) newError.serviceName = "Please select a service";
    if (!formData.customerName)
      newError.customerName = "Customer Name is required";
    if (!formData.phone || !/^\d{10}$/.test(formData.phone))
      newError.phone = "A valid 10-digit phone number is required";
    if (!formData.address) newError.address = "Address is required";
  

    setErrors(newError);
    return Object.keys(newError).length === 0;
  };

  const handleSubmit = () => {
    if (validation()) {
      const formattedDate = formData.bookingDate
        ? formData.bookingDate.format("DD-MM-YYYY")
        : null;
      const formattedTime = formData.bookingTime
        ? dayjs(formData.bookingTime).format("HH:mm:ss")
        : null;
      const bookingDetails = {
        ...formData,
        bookingDate: formattedDate,
        bookingTime: formattedTime,
      };
      onSubmit(bookingDetails);
      onClose();
    }
  };

  const today = dayjs();
  const maxDay = today.add(7, "day");

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle style={{ color: "#ff4d4d" }}>
        {booking ? "Edit Job" : "Add New Job"}
      </DialogTitle>
      <DialogContent>
        <Row>
          <Col lg={4}>
            <FormControl fullWidth margin="dense" error={!!errors.serviceName}>
              <InputLabel id="service-label">Service</InputLabel>
              <Select
                labelId="service-label"
                name="serviceName"
                value={formData.serviceName}
                label="Select Service"
                onChange={handleChange}
              >
                {serviceNamesWithIds.map((serviceName) => (
                  <MenuItem key={serviceName.id} value={serviceName.id}>
                    {serviceName.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Name"
              fullWidth
              margin="dense"
              name="customerName"
              value={formData.customerName}
              onChange={handleChange}
              error={!!errors.customerName}
              helperText={errors.customerName}
            />
            <TextField
              label="Phone"
              fullWidth
              margin="dense"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
            />
            <TextField
              label="Address"
              fullWidth
              margin="dense"
              name="address"
              multiline
              rows={3}
              value={formData.address}
              onChange={handleChange}
              error={!!errors.address}
              helperText={errors.address}
            />
        
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Booking Date"
                value={formData.bookingDate}
                format="DD-MM-YYYY"
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField {...params} fullWidth margin="dense" />
                )}
                sx={{ width: "100%", marginTop: "7px" }}
                minDate={today}
                maxDate={maxDay}
              />
              <TimePicker
                label="Booking Time"
                value={dayjs(formData.bookingTime, "HH:mm:ss")}
                slotProps={{
                  textField: {
                    error: false,
                  },
                }}
                onChange={handleTimeChange}
                renderInput={(params) => (
                  <TextField {...params} fullWidth margin="dense" />
                )}
                viewRenderers={{
                  hours: renderTimeViewClock,
                  minutes: renderTimeViewClock,
                  seconds: renderTimeViewClock,
                }}
                sx={{ width: "100%", marginTop: "7px" }}
              />
            </LocalizationProvider>
          </Col>
          <Col lg={8}>
            <MapLocation
              booking={booking}
              setLatitude={setLatitude}
              setLongitude={setLongitude}
              latitude={latitude}
              longitude={longitude}
            />
          </Col>
        </Row>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          color="error"
          style={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          style={{ textTransform: "none" }}
        >
          {booking ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingForm;
